import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, openFileInTab, openInTab } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import { money, todayIn, expenseColor } from "@/lib/helpers";
import ChatImage from "@/components/ChatImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileText, Paperclip, Loader2, X, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Calculs en centimes (comme le serveur) : 0,1 + 0,2 = 0,30 au centime près.
const cents = (v) => Math.round((parseFloat(v) || 0) * 100);
const euros = (c) => c / 100;
const MAX_CENTS = 100_000_000; // 1 000 000 €, plafond du serveur
const frDay = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "");
const isPdf = (type) => (type || "").includes("pdf");
// Ligne rouge sous un champ : pourquoi le bouton « Enregistrer » est grisé (comme au lot 19).
const FieldError = ({ msg, testid }) => (msg ? <p className="text-xs text-red-600" data-testid={testid}>{msg}</p> : null);
// Formats acceptés par le serveur. Sur iPhone, une liste précise fait convertir la photo HEIC en JPEG.
const ACCEPT = "image/jpeg,image/png,image/gif,image/webp,application/pdf";
const FUEL_HINT = "Uniquement le carburant des véhicules de la société. Un plein fait pour un client se saisit dans Compte carburant.";

// Catégorie affichée : celle des Réglages (avec sa couleur) ; vide = « Autre » ; ancienne catégorie hors liste
// = son nom tel quel, en gris.
const categoryOf = (label, cats) => {
  if (!label) return { label: "Autre", color: cats.find((c) => c.label === "Autre")?.color || "gray" };
  return cats.find((c) => c.label === label) || { label, color: "gray" };
};

const CategoryPill = ({ label, cats, testid }) => {
  const c = categoryOf(label, cats);
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full", expenseColor(c.color).pill)} data-testid={testid}>
      <span className={cn("h-1.5 w-1.5 rounded-full", expenseColor(c.color).dot)} />{c.label}
    </span>
  );
};

// Ouvre un justificatif : image en grand dans l'outil, PDF dans un nouvel onglet.
const attachmentOpener = (setBig) => async (path, type) => {
  if (!isPdf(type)) { setBig(path); return; }
  try { await openFileInTab(path); }
  catch { toast.error("Justificatif indisponible"); }
};

export default function Expenses() {
  const { settings } = useSettings();
  const cur = settings?.currency || "EUR";
  const [expenses, setExpenses] = useState([]);
  const [full, setFull] = useState({});
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);
  const [bigImage, setBigImage] = useState(null);
  // Mois en cours à l'heure de Monaco (fuseau des Réglages), comme les autres écrans.
  const [month, setMonth] = useState(() => todayIn(settings?.timezone).slice(0, 7));
  const openAttachment = attachmentOpener(setBigImage);
  const cats = full.expense_categories || [];

  const load = useCallback(() => {
    api.get("/expenses", { params: { month } }).then((r) => setExpenses(r.data));
  }, [month]);
  useEffect(() => { load(); }, [load]);
  // Catégories et moyens de paiement : réglages réservés aux gestionnaires (absents de /settings public).
  useEffect(() => { api.get("/settings/full").then((r) => setFull(r.data)).catch(() => setFull({})); }, []);

  const total = euros(expenses.reduce((sum, e) => sum + cents(e.amount), 0));

  // Export du mois affiché (étape C) : CSV téléchargé, PDF ouvert dans un nouvel onglet.
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/expenses/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `depenses-${month}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export CSV des dépenses généré");
    } catch { toast.error("Erreur lors de l'export"); }
  };
  const exportPdf = async () => {
    try { await openInTab("/expenses/export/pdf", { month }); }
    catch { toast.error("Erreur lors de l'export PDF"); }
  };

  const del = async () => {
    const e = toDelete; setToDelete(null);
    try { await api.delete(`/expenses/${e.id}`); toast.success("Dépense supprimée"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Comptabilité</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Dépenses</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 px-2.5 rounded-lg border border-border bg-card text-sm" data-testid="expense-month" />
          <Button variant="outline" className="gap-2 rounded-full" onClick={exportCsv} disabled={!expenses.length} title="Exporter les dépenses du mois (tableur)" data-testid="expense-export-csv"><Download className="h-4 w-4" /> CSV</Button>
          <Button variant="outline" className="gap-2 rounded-full" onClick={exportPdf} disabled={!expenses.length} title="Exporter les dépenses du mois (PDF)" data-testid="expense-export-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <Button className="gap-2 rounded-full" onClick={() => setDialog({ open: true, item: null })} data-testid="new-expense-button"><Plus className="h-4 w-4" /> Dépense</Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border">
        {expenses.length === 0 && <p className="p-6 text-sm text-muted-foreground">Aucune dépense ce mois-ci.</p>}
        {expenses.map((e) => (
          <div key={e.id} className="p-4 md:p-5 flex items-start gap-3" data-testid={`expense-row-${e.id}`}>
            {e.attachment && (
              <button type="button" onClick={() => openAttachment(e.attachment, e.attachment_type)} className="shrink-0" title="Voir le justificatif" data-testid={`expense-attachment-${e.id}`}>
                {isPdf(e.attachment_type)
                  ? <span className="h-12 w-12 rounded-lg border border-border bg-muted/50 grid place-items-center" data-testid={`expense-attachment-pdf-${e.id}`}><FileText className="h-5 w-5 text-muted-foreground" /></span>
                  : <ChatImage path={e.attachment} className="h-12 w-12 rounded-lg object-cover border border-border" />}
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="font-medium break-words min-w-0">{e.label}</div>
                <div className="font-display font-bold tabular-nums shrink-0" data-testid={`expense-amount-${e.id}`}>{money(e.amount, cur)}</div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span>{frDay(e.date)}</span>
                <CategoryPill label={e.category} cats={cats} testid={`expense-category-${e.id}`} />
                {e.supplier_ref && <span className="break-all" data-testid={`expense-ref-${e.id}`}>Facture n° {e.supplier_ref}</span>}
                {e.payment_method && <span data-testid={`expense-payment-${e.id}`}>{e.payment_method}</span>}
              </div>
            </div>
            <div className="flex items-center shrink-0 -mr-2 -mt-1">
              <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, item: e })} title="Modifier" data-testid={`expense-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setToDelete(e)} title="Supprimer" data-testid={`expense-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
        <div className="px-4 md:px-5 py-3 flex items-center justify-between gap-3 bg-muted/50" data-testid="expense-total">
          <span className="text-[10px] uppercase tracking-overline text-muted-foreground">Total du mois</span>
          <span className="font-display font-black text-xl tabular-nums" data-testid="expense-total-value">{money(total, cur)}</span>
        </div>
      </div>

      <ExpenseDialog state={dialog} setState={setDialog} cats={cats} methods={full.expense_payment_methods || []} onOpen={openAttachment} onSaved={load} />

      <Dialog open={!!bigImage} onOpenChange={(o) => { if (!o) setBigImage(null); }}>
        <DialogContent className="max-w-2xl" data-testid="expense-attachment-big">
          <DialogHeader><DialogTitle className="font-display">Justificatif</DialogTitle></DialogHeader>
          {bigImage && <ChatImage path={bigImage} className="w-full max-h-[75vh] object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="expense-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Supprimer cette dépense ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expense-delete-confirm-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="expense-delete-confirm-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const NO_METHOD = "__none__";
const FIELDS = ["date", "label", "category", "supplier_ref", "payment_method", "amount", "attachment"];

function ExpenseDialog({ state, setState, cats, methods, onOpen, onSaved }) {
  const voc = useVocab();
  const { settings } = useSettings();
  const empty = () => ({ date: todayIn(settings?.timezone), label: "", category: "", supplier_ref: "", payment_method: "",
    amount: "", attachment: "", attachment_type: "", attachment_name: "" });
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!state.open) return;
    const it = state.item;
    setForm(it ? { ...empty(), ...Object.fromEntries(FIELDS.map((k) => [k, it[k] ?? ""])), amount: String(it.amount ?? ""),
      attachment_type: it.attachment_type || "", attachment_name: it.attachment_name || "" } : empty());
  }, [state.open, state.item]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Une ancienne catégorie (ou un ancien moyen de paiement) hors des Réglages reste proposée telle quelle.
  const catOptions = form.category && !cats.some((c) => c.label === form.category) ? [...cats, { label: form.category, color: "gray" }] : cats;
  const methodOptions = form.payment_method && !methods.includes(form.payment_method) ? [...methods, form.payment_method] : methods;
  const isFuel = form.category.trim().toLowerCase() === "carburant";

  const c = cents(form.amount);
  const labelErr = !form.label.trim() ? "La désignation est obligatoire." : "";
  const amountErr = c <= 0 ? "Le montant doit être supérieur à 0 €." : c > MAX_CENTS ? "Le montant ne peut pas dépasser 1 000 000 €." : "";
  const invalid = !!(labelErr || amountErr);

  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { params: { kind: "document" }, headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachment: data.path, attachment_type: data.content_type || "", attachment_name: data.name || file.name }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Échec de l'envoi du justificatif"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.date) { toast.error("Indiquez la date"); return; }
    if (invalid) return;
    const payload = { date: form.date, label: form.label.trim(), category: form.category, supplier_ref: form.supplier_ref.trim(),
      payment_method: form.payment_method, amount: euros(c), attachment: form.attachment };
    setSaving(true);
    try {
      if (state.item) {
        // Modification : seuls les champs changés partent (une ancienne dépense garde le reste tel quel).
        const it = state.item;
        const changed = Object.fromEntries(Object.entries(payload).filter(([k, v]) =>
          k === "amount" ? cents(v) !== cents(it.amount) : v !== (it[k] ?? "")));
        if (Object.keys(changed).length) await api.patch(`/expenses/${it.id}`, changed);
      } else {
        await api.post("/expenses", payload);
      }
      toast.success("Dépense enregistrée"); onSaved(); setState({ open: false, item: null });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => setState({ ...state, open: o })}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{state.item ? "Modifier la dépense" : "Nouvelle dépense"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="expense-date" /></div>
          <div className="space-y-1.5"><Label>Désignation</Label>
            <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder={voc.expenseLabelPlaceholder} data-testid="expense-label" />
            <FieldError msg={labelErr} testid="expense-label-error" />
          </div>
          <div className="space-y-1.5"><Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Autre" /></SelectTrigger>
              <SelectContent>
                {catOptions.map((cat) => (
                  <SelectItem key={cat.label} value={cat.label}>
                    <span className="inline-flex items-center gap-2"><span className={cn("h-2.5 w-2.5 rounded-full shrink-0", expenseColor(cat.color).dot)} />{cat.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isFuel && <p className="text-xs text-muted-foreground" data-testid="expense-fuel-hint">{FUEL_HINT}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>N° de facture</Label><Input value={form.supplier_ref} onChange={(e) => set("supplier_ref", e.target.value)} placeholder="Du fournisseur" data-testid="expense-supplier-ref" /></div>
            <div className="space-y-1.5"><Label>Paiement</Label>
              <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v === NO_METHOD ? "" : v)}>
                <SelectTrigger data-testid="expense-payment"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_METHOD}>Non renseigné</SelectItem>
                  {methodOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Montant</Label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" data-testid="expense-amount" />
            <FieldError msg={amountErr} testid="expense-amount-error" />
          </div>
          <div className="space-y-1.5">
            <Label>Justificatif (facultatif)</Label>
            <input type="file" accept={ACCEPT} ref={fileRef} onChange={upload} className="hidden" data-testid="expense-attachment-input" />
            {form.attachment ? (
              <div className="flex items-center gap-3" data-testid="expense-attachment-preview">
                <button type="button" onClick={() => onOpen(form.attachment, form.attachment_type)} className="shrink-0" title="Voir le justificatif" data-testid="expense-attachment-open">
                  {isPdf(form.attachment_type)
                    ? <span className="h-20 w-20 rounded-lg border border-border bg-muted/50 grid place-items-center" data-testid="expense-attachment-preview-pdf"><FileText className="h-7 w-7 text-muted-foreground" /></span>
                    : <ChatImage path={form.attachment} className="h-20 w-20 rounded-lg object-cover border border-border" />}
                </button>
                <div className="min-w-0 space-y-1">
                  {form.attachment_name && <div className="text-xs text-muted-foreground break-all">{form.attachment_name}</div>}
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs text-accent flex items-center gap-1" data-testid="expense-attachment-replace">
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Remplacer
                    </button>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, attachment: "", attachment_type: "", attachment_name: "" }))} className="text-xs text-muted-foreground flex items-center gap-1" data-testid="expense-attachment-remove"><X className="h-3.5 w-3.5" /> Retirer</button>
                  </div>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full h-14 rounded-xl border border-dashed border-border grid place-items-center text-xs text-muted-foreground hover:border-accent" data-testid="expense-attachment-add">
                <span className="flex items-center gap-1.5">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Ajouter une photo ou un PDF</span>
              </button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState({ open: false, item: null })}>Annuler</Button>
          <Button onClick={save} disabled={saving || uploading || invalid} data-testid="expense-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
